import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import "./ClientDetail.css";

const TABS = [
    { key: "profile", label: "Profile & Scope" },
    { key: "projects", label: "Projects" },
    { key: "quotes", label: "Quotes" },
    { key: "invoices", label: "Invoices" },
];

const BLANK_PROJECT = {
    title: "",
    description: "",
    scope: "",
    status: "active",
    start_date: "",
    end_date: "",
    budget_cad: "",
};

const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || "";
};

const formatCAD = (n) =>
    n != null
        ? new Intl.NumberFormat("en-CA", {
              style: "currency",
              currency: "CAD",
          }).format(n)
        : "—";

const ClientDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { impersonate } = useAuth();

    const [tab, setTab] = useState("profile");
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveMsg, setSaveMsg] = useState("");

    // Profile form
    const [profile, setProfile] = useState({});
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileError, setProfileError] = useState("");

    // Projects
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [showProjectForm, setShowProjectForm] = useState(false);
    const [projectForm, setProjectForm] = useState({ ...BLANK_PROJECT });
    const [savingProject, setSavingProject] = useState(false);
    const [projectError, setProjectError] = useState("");

    // Quotes
    const [quotes, setQuotes] = useState([]);
    const [quotesLoading, setQuotesLoading] = useState(false);

    // Invoices
    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);

    // Load client
    const loadClient = useCallback(() => {
        setLoading(true);
        api.getClient(id)
            .then((res) => {
                const c = res.client || res;
                setClient(c);
                setProfile({
                    name: c.name || "",
                    email: c.email || "",
                    company: c.company || "",
                    phone: c.phone || "",
                    website: c.website || "",
                    preferred_language: c.preferred_language || "en",
                    account_status: c.account_status || "active",
                    notes: c.notes || "",
                });
            })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        loadClient();
    }, [loadClient]);

    // Lazy-load tab data
    useEffect(() => {
        if (tab === "projects" && projects.length === 0) {
            setProjectsLoading(true);
            api.clientProjects(id)
                .then((res) => setProjects(res.projects || []))
                .finally(() => setProjectsLoading(false));
        }
        if (tab === "quotes" && quotes.length === 0) {
            setQuotesLoading(true);
            api.quotes({ client_id: id })
                .then((res) => setQuotes(res.quotes?.data || res.quotes || []))
                .finally(() => setQuotesLoading(false));
        }
        if (tab === "invoices" && invoices.length === 0) {
            setInvoicesLoading(true);
            api.invoices({ client_id: id })
                .then((res) =>
                    setInvoices(res.invoices?.data || res.invoices || []),
                )
                .finally(() => setInvoicesLoading(false));
        }
    }, [tab, id, projects.length, quotes.length, invoices.length]);

    const handleProfileChange = (e) =>
        setProfile((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setSavingProfile(true);
        setProfileError("");
        setSaveMsg("");
        try {
            const res = await api.updateClient(id, profile);
            setClient(res.client || res);
            setSaveMsg("Saved.");
            setTimeout(() => setSaveMsg(""), 3000);
        } catch (err) {
            setProfileError(err.message);
        } finally {
            setSavingProfile(false);
        }
    };

    const [resetPw, setResetPw] = useState(null);

    const handleResetPassword = async () => {
        if (
            !window.confirm(
                `Reset ${client.name}'s password? Their current password will stop working immediately.`,
            )
        )
            return;
        try {
            const res = await api.resetClientPassword(id);
            setResetPw(res.temporary_password);
        } catch (err) {
            alert(err.message);
        }
    };

    const handleArchive = async () => {
        if (
            !window.confirm(
                `Archive ${client.name}? They will lose portal access but all data is kept.`,
            )
        )
            return;
        try {
            await api.archiveClient(id);
            loadClient();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleUnarchive = async () => {
        try {
            await api.updateClient(id, { account_status: "active" });
            loadClient();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDelete = async () => {
        if (
            !window.confirm(
                `Permanently delete ${client.name}? This cannot be undone.`,
            )
        )
            return;
        if (
            !window.confirm(
                `Final confirmation: delete ALL data for ${client.name}?`,
            )
        )
            return;
        try {
            await api.deleteClient(id);
            navigate("/admin/clients");
        } catch (err) {
            alert(err.message);
        }
    };

    // Project creation
    const handleProjectChange = (e) =>
        setProjectForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleProjectSubmit = async (e) => {
        e.preventDefault();
        setSavingProject(true);
        setProjectError("");
        try {
            await api.createProject({
                ...projectForm,
                client_id: id,
                budget_cad: projectForm.budget_cad
                    ? parseFloat(projectForm.budget_cad)
                    : null,
            });
            setShowProjectForm(false);
            setProjectForm({ ...BLANK_PROJECT });
            setProjectsLoading(true);
            api.clientProjects(id)
                .then((res) => setProjects(res.projects || []))
                .finally(() => setProjectsLoading(false));
        } catch (err) {
            setProjectError(err.message);
        } finally {
            setSavingProject(false);
        }
    };

    const handleDeleteProject = async (projId) => {
        if (!window.confirm("Delete this project?")) return;
        await api.deleteProject(projId);
        setProjects((prev) => prev.filter((p) => p.id !== projId));
    };

    if (loading) {
        return (
            <div className="client-detail__loading">
                <p>{t("common.loading")}</p>
            </div>
        );
    }

    if (!client) {
        return (
            <div className="client-detail__loading">
                <p>Client not found.</p>
            </div>
        );
    }

    return (
        <div className="client-detail">
            {/* Back + header */}
            <div className="client-detail__back">
                <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => navigate("/admin/clients")}
                >
                    ← Clients
                </button>
            </div>

            <div className="client-detail__header">
                <div className="client-detail__header-info">
                    <h2 className="client-detail__name">{client.name}</h2>
                    {client.company && (
                        <p className="client-detail__company">
                            {client.company}
                        </p>
                    )}
                </div>
                <div className="client-detail__header-actions">
                    {client.account_status === "active" && (
                        <button
                            className="btn btn--ghost btn--sm"
                            onClick={async () => {
                                try {
                                    await impersonate(id);
                                } catch (err) {
                                    alert(err.message);
                                }
                            }}
                            title="Open the portal exactly as this client sees it"
                        >
                            <FontAwesomeIcon icon={faEye} /> View as Client
                        </button>
                    )}
                    <StatusBadge status={client.account_status} />
                </div>
            </div>

            {/* Tabs */}
            <div className="client-detail__tabs">
                {TABS.map((tabItem) => (
                    <button
                        key={tabItem.key}
                        className={`client-detail__tab ${tab === tabItem.key ? "client-detail__tab--active" : ""}`}
                        onClick={() => setTab(tabItem.key)}
                    >
                        {tabItem.label}
                    </button>
                ))}
            </div>

            {/* ── Profile & Scope ─────────────────────────────────────── */}
            {tab === "profile" && (
                <div className="client-detail__panel">
                    <form onSubmit={handleProfileSave}>
                        <div className="client-detail__form-grid">
                            <div className="form-group">
                                <label>Full Name *</label>
                                <input
                                    name="name"
                                    value={profile.name}
                                    onChange={handleProfileChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={profile.email}
                                    onChange={handleProfileChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Company</label>
                                <input
                                    name="company"
                                    value={profile.company}
                                    onChange={handleProfileChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone</label>
                                <input
                                    name="phone"
                                    value={profile.phone}
                                    onChange={handleProfileChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>Website</label>
                                <input
                                    name="website"
                                    value={profile.website}
                                    onChange={handleProfileChange}
                                    placeholder="https://"
                                />
                            </div>
                            <div className="form-group">
                                <label>Language</label>
                                <select
                                    name="preferred_language"
                                    value={profile.preferred_language}
                                    onChange={handleProfileChange}
                                >
                                    <option value="en">English</option>
                                    <option value="fr">French</option>
                                    <option value="zh">Mandarin</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Account Status</label>
                                <select
                                    name="account_status"
                                    value={profile.account_status}
                                    onChange={handleProfileChange}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="active">Active</option>
                                    <option value="declined">Declined</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Notes / Scope</label>
                            <textarea
                                name="notes"
                                value={profile.notes}
                                onChange={handleProfileChange}
                                rows={6}
                                placeholder="Internal notes, project scope, client context…"
                            />
                        </div>
                        {profileError && (
                            <p className="error-message">{profileError}</p>
                        )}
                        <div className="client-detail__form-actions">
                            <button
                                type="submit"
                                className="btn btn--primary btn--sm"
                                disabled={savingProfile}
                            >
                                {savingProfile ? "Saving…" : "Save Changes"}
                            </button>
                            {saveMsg && (
                                <span className="client-detail__save-msg">
                                    {saveMsg}
                                </span>
                            )}
                        </div>
                    </form>

                    {/* Onboarding data (read-only) */}
                    {client.onboarding_response && (
                        <div className="client-detail__onboarding card">
                            <h4>Onboarding Application</h4>
                            <div className="client-detail__onboarding-grid">
                                {[
                                    [
                                        "Business Name",
                                        client.onboarding_response
                                            .business_name,
                                    ],
                                    [
                                        "Business Type",
                                        client.onboarding_response
                                            .business_type,
                                    ],
                                    [
                                        "Service Interest",
                                        client.onboarding_response
                                            .service_interest,
                                    ],
                                    [
                                        "Budget Range",
                                        client.onboarding_response.budget_range,
                                    ],
                                    [
                                        "Timeline",
                                        client.onboarding_response.timeline,
                                    ],
                                    [
                                        "Heard From",
                                        client.onboarding_response.heard_from,
                                    ],
                                ]
                                    .filter(([, v]) => v)
                                    .map(([label, value]) => (
                                        <div key={label}>
                                            <dt>{label}</dt>
                                            <dd>{value}</dd>
                                        </div>
                                    ))}
                            </div>
                            {client.onboarding_response.project_description && (
                                <div className="client-detail__onboarding-desc">
                                    <dt>Project Description</dt>
                                    <dd>
                                        {
                                            client.onboarding_response
                                                .project_description
                                        }
                                    </dd>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Password reset */}
                    <div className="client-detail__danger card">
                        <h4>Access</h4>
                        <div className="client-detail__danger-actions">
                            <div className="client-detail__danger-item">
                                <div>
                                    <strong>Reset Password</strong>
                                    <p>
                                        Generates a new temporary password and
                                        signs the client out everywhere. Share
                                        it with them securely — it is shown only
                                        once.
                                    </p>
                                    {resetPw && (
                                        <p className="client-detail__temp-pw">
                                            Temporary password:{" "}
                                            <code>{resetPw}</code>{" "}
                                            <button
                                                type="button"
                                                className="btn btn--ghost btn--sm"
                                                onClick={() =>
                                                    navigator.clipboard.writeText(
                                                        resetPw,
                                                    )
                                                }
                                            >
                                                Copy
                                            </button>
                                        </p>
                                    )}
                                </div>
                                <button
                                    className="btn btn--ghost btn--sm"
                                    onClick={handleResetPassword}
                                >
                                    Reset Password
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Danger zone */}
                    <div className="client-detail__danger card">
                        <h4>Danger Zone</h4>
                        <div className="client-detail__danger-actions">
                            {client.account_status !== "archived" ? (
                                <div className="client-detail__danger-item">
                                    <div>
                                        <strong>Archive Client</strong>
                                        <p>
                                            Disable portal access. All data is
                                            preserved and can be restored.
                                        </p>
                                    </div>
                                    <button
                                        className="btn btn--ghost btn--sm"
                                        onClick={handleArchive}
                                    >
                                        Archive
                                    </button>
                                </div>
                            ) : (
                                <div className="client-detail__danger-item">
                                    <div>
                                        <strong>Unarchive Client</strong>
                                        <p>
                                            Restore portal access and set status
                                            to Active.
                                        </p>
                                    </div>
                                    <button
                                        className="btn btn--primary btn--sm"
                                        onClick={handleUnarchive}
                                    >
                                        Unarchive
                                    </button>
                                </div>
                            )}
                            <div className="client-detail__danger-item client-detail__danger-item--delete">
                                <div>
                                    <strong>Delete Client</strong>
                                    <p>
                                        Permanently remove this client and all
                                        their quotes, invoices, and projects.
                                        This cannot be undone.
                                    </p>
                                </div>
                                <button
                                    className="btn btn--danger btn--sm"
                                    onClick={handleDelete}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Projects ─────────────────────────────────────────────── */}
            {tab === "projects" && (
                <div className="client-detail__panel">
                    <div className="client-detail__tab-header">
                        <h3>Projects</h3>
                        <button
                            className="btn btn--primary btn--sm"
                            onClick={() => setShowProjectForm((v) => !v)}
                        >
                            {showProjectForm ? "Cancel" : "+ New Project"}
                        </button>
                    </div>

                    {showProjectForm && (
                        <div
                            className="card"
                            style={{ padding: "16px", marginBottom: "16px" }}
                        >
                            <form onSubmit={handleProjectSubmit}>
                                <div className="client-detail__form-grid">
                                    <div className="form-group">
                                        <label>Title *</label>
                                        <input
                                            name="title"
                                            value={projectForm.title}
                                            onChange={handleProjectChange}
                                            required
                                            placeholder="Website Redesign"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select
                                            name="status"
                                            value={projectForm.status}
                                            onChange={handleProjectChange}
                                        >
                                            {[
                                                "active",
                                                "on_hold",
                                                "completed",
                                                "cancelled",
                                            ].map((s) => (
                                                <option key={s} value={s}>
                                                    {s.replace("_", " ")}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Budget (CAD)</label>
                                        <input
                                            type="number"
                                            name="budget_cad"
                                            value={projectForm.budget_cad}
                                            onChange={handleProjectChange}
                                            min="0"
                                            step="0.01"
                                            placeholder="5000.00"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Start Date</label>
                                        <input
                                            type="date"
                                            name="start_date"
                                            value={projectForm.start_date}
                                            onChange={handleProjectChange}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>End Date</label>
                                        <input
                                            type="date"
                                            name="end_date"
                                            value={projectForm.end_date}
                                            onChange={handleProjectChange}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        name="description"
                                        value={projectForm.description}
                                        onChange={handleProjectChange}
                                        rows={3}
                                        placeholder="Brief overview…"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Scope</label>
                                    <textarea
                                        name="scope"
                                        value={projectForm.scope}
                                        onChange={handleProjectChange}
                                        rows={4}
                                        placeholder="Deliverables, milestones…"
                                    />
                                </div>
                                {projectError && (
                                    <p className="error-message">
                                        {projectError}
                                    </p>
                                )}
                                <button
                                    type="submit"
                                    className="btn btn--primary btn--sm"
                                    disabled={savingProject}
                                >
                                    {savingProject
                                        ? "Saving…"
                                        : "Create Project"}
                                </button>
                            </form>
                        </div>
                    )}

                    {projectsLoading ? (
                        <p className="dash-table__empty">
                            {t("common.loading")}
                        </p>
                    ) : projects.length === 0 ? (
                        <p className="dash-table__empty">No projects yet.</p>
                    ) : (
                        <div className="client-detail__project-list">
                            {projects.map((p) => (
                                <div
                                    key={p.id}
                                    className="client-detail__project-row card"
                                >
                                    <div className="client-detail__project-info">
                                        <strong
                                            className="client-detail__project-title"
                                            onClick={() =>
                                                navigate(
                                                    `/admin/projects/${p.id}`,
                                                )
                                            }
                                        >
                                            {p.title}
                                        </strong>
                                        {p.description && (
                                            <p>{stripHtml(p.description)}</p>
                                        )}
                                        <div className="client-detail__project-meta">
                                            {p.budget_cad != null && (
                                                <span>
                                                    {formatCAD(p.budget_cad)}
                                                </span>
                                            )}
                                            {p.start_date && (
                                                <span>
                                                    {p.start_date}
                                                    {p.end_date
                                                        ? ` → ${p.end_date}`
                                                        : ""}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="client-detail__project-side">
                                        <StatusBadge status={p.status} />
                                        <div className="client-detail__project-actions">
                                            <button
                                                className="btn btn--ghost btn--xs"
                                                onClick={() =>
                                                    navigate(
                                                        `/admin/projects/${p.id}`,
                                                    )
                                                }
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn--danger btn--xs"
                                                onClick={() =>
                                                    handleDeleteProject(p.id)
                                                }
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Quotes ────────────────────────────────────────────────── */}
            {tab === "quotes" && (
                <div className="client-detail__panel">
                    <div className="client-detail__tab-header">
                        <h3>Quotes</h3>
                        <button
                            className="btn btn--primary btn--sm"
                            onClick={() =>
                                navigate(`/admin/quotes/new?client_id=${id}`)
                            }
                        >
                            + New Quote
                        </button>
                    </div>
                    {quotesLoading ? (
                        <p className="dash-table__empty">
                            {t("common.loading")}
                        </p>
                    ) : (
                        <div
                            className="card"
                            style={{ padding: 0, overflow: "hidden" }}
                        >
                            <table className="dash-table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Status</th>
                                        <th>Total</th>
                                        <th>Expires</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quotes.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="dash-table__empty"
                                            >
                                                No quotes yet.
                                            </td>
                                        </tr>
                                    )}
                                    {quotes.map((q) => (
                                        <tr key={q.id}>
                                            <td>{q.title}</td>
                                            <td>
                                                <StatusBadge
                                                    status={q.status}
                                                />
                                            </td>
                                            <td>{formatCAD(q.total)}</td>
                                            <td className="dash-table__muted">
                                                {q.expires_at
                                                    ? new Date(
                                                          q.expires_at,
                                                      ).toLocaleDateString(
                                                          "en-CA",
                                                      )
                                                    : "—"}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn--ghost btn--xs"
                                                    onClick={() =>
                                                        navigate(
                                                            `/admin/quotes/${q.id}`,
                                                        )
                                                    }
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Invoices ──────────────────────────────────────────────── */}
            {tab === "invoices" && (
                <div className="client-detail__panel">
                    <div className="client-detail__tab-header">
                        <h3>Invoices</h3>
                        <button
                            className="btn btn--primary btn--sm"
                            onClick={() =>
                                navigate(`/admin/invoices/new?client_id=${id}`)
                            }
                        >
                            + New Invoice
                        </button>
                    </div>
                    {invoicesLoading ? (
                        <p className="dash-table__empty">
                            {t("common.loading")}
                        </p>
                    ) : (
                        <div
                            className="card"
                            style={{ padding: 0, overflow: "hidden" }}
                        >
                            <table className="dash-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Status</th>
                                        <th>Total</th>
                                        <th>Due</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="dash-table__empty"
                                            >
                                                No invoices yet.
                                            </td>
                                        </tr>
                                    )}
                                    {invoices.map((inv) => (
                                        <tr key={inv.id}>
                                            <td className="dash-table__mono">
                                                {inv.invoice_number}
                                            </td>
                                            <td>
                                                <StatusBadge
                                                    status={inv.status}
                                                />
                                            </td>
                                            <td>{formatCAD(inv.total)}</td>
                                            <td className="dash-table__muted">
                                                {inv.due_date
                                                    ? new Date(
                                                          inv.due_date,
                                                      ).toLocaleDateString(
                                                          "en-CA",
                                                      )
                                                    : "—"}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn--ghost btn--xs"
                                                    onClick={() =>
                                                        navigate(
                                                            `/admin/invoices/${inv.id}`,
                                                        )
                                                    }
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientDetail;
